import {
  AdyenCheckout,
  Card,
  type CardConfiguration,
  type CoreConfiguration,
  type SubmitData,
  type UIElement,
} from "@adyen/adyen-web";
import React, { HTMLAttributes, useEffect, useState } from "react";
import useGeneralStore from "../../../store/general";
import BaseButton from "../baseButton";

const ADYEN_CLIENT_KEY = process.env.REACT_APP_ADYEN_CLIENT_KEY;
const ADYEN_ENV = process.env.REACT_APP_ENV === "production" ? "live" : "test";

export interface IAdyenCreditCardInfo {
  encryptedCardNumber: string;
  encryptedSecurityCode: string;
  encryptedExpiryYear: string;
  encryptedExpiryMonth: string;
  clientData?: string;
}

interface IAdyenCreditCardElement {
  onCardElementSubmit: (value: IAdyenCreditCardInfo) => void;
  buttonDisabled: boolean;
}

type TAdyenCreditCardElementProps = IAdyenCreditCardElement &
  HTMLAttributes<HTMLDivElement>;

const AdyenCreditCardElement: React.FC<TAdyenCreditCardElementProps> = (
  props
) => {
  const { onCardElementSubmit, buttonDisabled, ...otherProps } = props;
  const [cardElement, setCardElement] = useState<Card | undefined>(undefined);
  const setGlobalLoadingState = useGeneralStore((state) => state.setIsLoading);
  const [isCardAllValid, setIsCardAllValid] = useState(false);

  const initAdyen = async () => {
    try {
      console.log("adyen init");

      // Configuration for AdyenCheckout
      const configuration: CoreConfiguration = {
        clientKey: ADYEN_CLIENT_KEY,
        environment: ADYEN_ENV,
        locale: "en-US", // Locale for the payment form
        countryCode: "HK",
        showPayButton: false,

        risk: {
          enabled: true,
        },
        analytics: {
          enabled: true,
        },
        onError(error, component) {
          console.error("Error", error);
        },
      };

      // Initialize AdyenCheckout
      const checkout = await AdyenCheckout(configuration);

      const customCardConfig: CardConfiguration = {
        styles: {
          error: {
            color: "red",
          },
          validated: {
            color: "green",
          },
          placeholder: {
            fontSize: "12px",
            color: "black",
            fontWeight: "500",
          },
        },

        placeholders: {
          cardNumber: "Card number",
          expiryDate: "MM / YY",
          securityCodeThreeDigits: "CVC",
        },
        showContextualElement: false,
        onSubmit: async (state, component) => {
          await cardInfoSubmitCallbackHandler(state, component);
        },

        onAllValid(event) {
          setIsCardAllValid(true);
        },
      };

      const customCard = new Card(checkout, customCardConfig).mount(
        "#component-container"
      );

      setCardElement(customCard);
    } catch (error) {
      setGlobalLoadingState(false);

      console.log("init error1");
      console.log(error);
    } finally {
      setGlobalLoadingState(false);
    }
  };

  const cardInfoSubmitCallbackHandler = async (
    state: SubmitData,
    cardElement: UIElement
  ) => {
    try {
      const cardInfo: IAdyenCreditCardInfo = {
        encryptedCardNumber: state.data.paymentMethod.encryptedCardNumber,
        encryptedSecurityCode: state.data.paymentMethod.encryptedSecurityCode,
        encryptedExpiryYear: state.data.paymentMethod.encryptedExpiryYear,
        encryptedExpiryMonth: state.data.paymentMethod.encryptedExpiryMonth,
        clientData: state.data.riskData?.clientData,
        // userId: userInfo?.userId,
      };
      console.log("onCardElementSubmit");
      console.log(cardInfo);
      onCardElementSubmit(cardInfo);
      cardElement.setStatus("ready");
    } catch (error) {
      cardElement.setStatus("error");
    } finally {
      cardElement.setStatus("ready");
    }
  };

  const submitCardDetailHandler = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (cardElement) {
      cardElement.submit();
    }
  };

  // init adyen card element when tab changed
  useEffect(() => {
    initAdyen();
  }, []);

  return (
    <div className="flex flex-col gap-4 text-white" {...otherProps}>
      <div
        id="component-container"
        className="flex-1 flex text-white min-h-[6.25rem]"
      ></div>
      <BaseButton
        onClick={submitCardDetailHandler}
        disabled={!isCardAllValid || buttonDisabled}
        minWidth="w-full"
        disabledStyle="disabled:bg-mainYellow disabled:bg-opacity-60"
      >
        訂閱
      </BaseButton>
    </div>
  );
};

export default AdyenCreditCardElement;
